import { getApiClient } from "../http/client";
import type { DataResponse, RequestOptions } from "../http/types";
import type {
  Cctv,
  CctvPlayback,
  CctvPtzRequest,
  CctvUpdateRequest,
  DetectionType,
  PtzPreset,
} from "./types";

function normalizeList(raw: unknown): Cctv[] {
  const d = (raw as { data?: unknown })?.data ?? raw;
  if (Array.isArray(d)) return d as Cctv[];
  const content = (d as { content?: Cctv[] })?.content;
  return content ?? [];
}

export const cctvService = {
  list: async (siteId?: number): Promise<Cctv[]> => {
    const res = await getApiClient().get<unknown>("/cctvs", { params: { siteId } });
    return normalizeList(res);
  },

  /** CV 감지 항목 카탈로그 — 수정 화면 선택지. 백엔드에서 항목이 늘면 자동 반영 */
  detectionTypes: async (): Promise<DetectionType[]> => {
    const res = await getApiClient().get<unknown>("/cctvs/detection-types");
    const d = (res as { data?: unknown })?.data ?? res;
    return Array.isArray(d) ? (d as DetectionType[]) : [];
  },

  get: async (id: number): Promise<Cctv> => {
    const res = (await getApiClient().get<DataResponse<Cctv>>(`/cctvs/${id}`)) as unknown;
    return ((res as { data?: Cctv })?.data ?? res) as Cctv;
  },

  update: async (id: number, body: CctvUpdateRequest): Promise<void> => {
    await getApiClient().put<void>(`/cctvs/${id}`, body);
  },

  remove: async (id: number): Promise<void> => {
    await getApiClient().delete<void>(`/cctvs/${id}`);
  },

  /** DELETE /cctvs — PK 목록 일괄 삭제 (존재하지 않는 ID 는 백엔드가 무시) */
  removeBulk: async (ids: number[]): Promise<void> => {
    await getApiClient().delete<void>("/cctvs", { ids });
  },

  /**
   * PUT /cctvs/cv — CV 사용 집합 멱등 교체.
   * ids 가 곧 cv=true 전체 집합이 된다 (포함 → true, 나머지 전부 → false).
   * 호출처가 "기존 cv=true 전체 ± 선택" 을 계산해서 넘겨야 한다.
   */
  setCvEnabled: async (ids: number[]): Promise<void> => {
    await getApiClient().put<void>("/cctvs/cv", { ids });
  },

  ptzMove: async (id: number, body: CctvPtzRequest): Promise<void> => {
    await getApiClient().post<void>(`/cctvs/${id}/ptz`, body);
  },

  ptzStop: async (id: number): Promise<void> => {
    await getApiClient().post<void>(`/cctvs/${id}/ptz/stop`);
  },

  ptzPresets: async (id: number): Promise<PtzPreset[]> => {
    const res = await getApiClient().get<DataResponse<PtzPreset[]>>(`/cctvs/${id}/ptz/presets`);
    return Array.isArray(res.data) ? res.data : [];
  },

  ptzPresetSave: async (id: number, name: string): Promise<void> => {
    await getApiClient().post<void>(`/cctvs/${id}/ptz/presets`, { name });
  },

  ptzPresetGoto: async (id: number, token: string): Promise<void> => {
    await getApiClient().post<void>(`/cctvs/${id}/ptz/presets/${encodeURIComponent(token)}/goto`);
  },

  ptzPresetDelete: async (id: number, token: string): Promise<void> => {
    await getApiClient().delete<void>(`/cctvs/${id}/ptz/presets/${encodeURIComponent(token)}`);
  },

  /** 진행률을 재려면 몸통을 직접 읽어야 해서 `Response` 를 그대로 넘긴다. */
  download: (
    cctvId: number,
    startDate: string,
    endDate: string,
    options?: RequestOptions
  ): Promise<Response> =>
    getApiClient().stream(`/cctvs/${cctvId}/download`, {
      ...options,
      params: { startDate, endDate },
    }),

  /** 녹화 재생 경로를 연다. 돌려받은 `pathName` 으로 WHEP 스트림을 잡는다. */
  requestPlayback: async (cctvId: number, startDate: string, endDate: string): Promise<string> => {
    const res = await getApiClient().post<DataResponse<CctvPlayback>>(`/cctvs/${cctvId}/playback`, {
      startDate,
      endDate,
    });
    return res.data.pathName;
  },
};
